import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { FaSync, FaExclamationCircle, FaUnlink, FaToggleOn, FaToggleOff, FaRobot } from 'react-icons/fa';
import { formatFileSize } from '../../utils/formatters';
import '../../styles/Settings.css';

const API = generateClient();

const Settings = () => {
  const [settings, setSettings] = useState({
    aiOrganizeEnabled: true,
    aiSuggestions: true,
    notificationsEnabled: true,
    storageLimit: 5368709120, // 5GB
    storageUsed: 2147483648, // 2GB
    mmfConnected: true,
    mmfUsername: 'testuser',
    defaultView: 'folders',
    metadataSync: 'weekly',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      // DEVELOPMENT ONLY: Use mock data
      // In a real implementation, this would call the API
      
      // const response = await API.get('mmfBackupApi', '/user/settings', {});
      // setSettings(response);
      
      // Simulate API call with a delay
      setTimeout(() => {
        setSettings({
          aiOrganizeEnabled: true,
          aiSuggestions: true,
          notificationsEnabled: true,
          storageLimit: 5368709120, // 5GB
          storageUsed: 2147483648, // 2GB
          mmfConnected: true,
          mmfUsername: 'testuser',
          defaultView: 'folders',
          metadataSync: 'weekly',
        });
        setLoading(false);
      }, 500);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      // In a real implementation, this would call the API
      // await API.put('mmfBackupApi', '/user/settings', {
      //   body: settings
      // });
      
      // Simulate API call with a delay
      setTimeout(() => {
        alert('Settings saved successfully!');
        setSaving(false);
      }, 500);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
      setSaving(false);
    }
  };
  
  const disconnectMMF = async () => {
    if (window.confirm('Are you sure you want to disconnect your MyMiniFactory account? You will need to reconnect to continue accessing your files.')) {
      try {
        // In a real implementation, this would call the API
        // await API.post('mmfBackupApi', '/auth/disconnect', {});
        
        setSettings({
          ...settings,
          mmfConnected: false,
          mmfUsername: '',
        });
        alert('MyMiniFactory account disconnected successfully.');
      } catch (err) {
        console.error('Error disconnecting account:', err);
        alert('Failed to disconnect account: ' + err.message);
      }
    }
  };
  
  const handleToggle = (setting) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting]
    });
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({
      ...settings,
      [name]: value
    });
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <FaSync className="fa-spin" />
        <p>Loading settings...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <FaExclamationCircle />
        <h2>Error Loading Settings</h2>
        <p>{error}</p>
        <button onClick={fetchSettings} className="retry-button">Retry</button>
      </div>
    );
  }
  
  return (
    <div className="settings-container">
      <h2>Settings</h2>
      
      <div className="settings-section">
        <h3>MyMiniFactory Connection</h3>
        <div className="connection-status">
          {settings.mmfConnected ? (
            <>
              <div className="connected-info">
                <span className="connection-badge connected">Connected</span>
                <p>Connected as: <strong>{settings.mmfUsername}</strong></p>
              </div>
              <button onClick={disconnectMMF} className="disconnect-button">
                <FaUnlink /> Disconnect Account
              </button>
            </>
          ) : (
            <>
              <span className="connection-badge disconnected">Disconnected</span>
              <p>You need to connect your MyMiniFactory account to use this app.</p>
              <a href="/auth/mmf-connect" className="connect-button">Connect to MMF</a>
            </>
          )}
        </div>
      </div>
      
      <div className="settings-section">
        <h3>AI & Organization</h3>
        
        <div className="setting-item">
          <div className="setting-label">
            <span>AI File Organization</span>
            <p className="setting-description">
              Enable AI to automatically categorize and organize your files into logical folders
            </p>
          </div>
          <button 
            className={`toggle-button ${settings.aiOrganizeEnabled ? 'on' : 'off'}`}
            onClick={() => handleToggle('aiOrganizeEnabled')}
          >
            {settings.aiOrganizeEnabled ? <FaToggleOn /> : <FaToggleOff />}
          </button>
        </div>
        
        <div className="setting-item">
          <div className="setting-label">
            <span>AI Suggestions</span>
            <p className="setting-description">
              Show AI suggestions for organizing your files as you work
            </p>
          </div>
          <button 
            className={`toggle-button ${settings.aiSuggestions ? 'on' : 'off'}`}
            onClick={() => handleToggle('aiSuggestions')}
          >
            {settings.aiSuggestions ? <FaToggleOn /> : <FaToggleOff />}
          </button>
        </div>
        
        <div className="setting-item">
          <div className="setting-label">
            <span>Default View</span>
            <p className="setting-description">
              Choose which view to show by default when viewing your files
            </p>
          </div>
          <select 
            name="defaultView" 
            value={settings.defaultView}
            onChange={handleChange}
            className="setting-select"
          >
            <option value="folders">Folder View</option>
            <option value="list">List View</option>
            <option value="ai">AI Organization</option>
          </select>
        </div>
      </div>
      
      <div className="settings-section">
        <h3>Sync & Notifications</h3>
        
        <div className="setting-item">
          <div className="setting-label">
            <span>Metadata Sync Frequency</span>
            <p className="setting-description">
              How often to check MyMiniFactory for new or updated files
            </p>
          </div>
          <select 
            name="metadataSync" 
            value={settings.metadataSync}
            onChange={handleChange}
            className="setting-select"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="manual">Manual Only</option>
          </select>
        </div>
        
        <div className="setting-item">
          <div className="setting-label">
            <span>Notifications</span>
            <p className="setting-description">
              Receive notifications about new files and organization activity
            </p>
          </div>
          <button 
            className={`toggle-button ${settings.notificationsEnabled ? 'on' : 'off'}`}
            onClick={() => handleToggle('notificationsEnabled')}
          >
            {settings.notificationsEnabled ? <FaToggleOn /> : <FaToggleOff />}
          </button>
        </div>
      </div>
      
      <div className="settings-section">
        <h3>Storage</h3>
        <div className="storage-info">
          <div className="storage-usage">
            <div className="storage-bar">
              <div 
                className="storage-used" 
                style={{ 
                  width: `${Math.min(100, (settings.storageUsed / settings.storageLimit) * 100)}%` 
                }}
              ></div>
            </div>
            <p>
              {formatFileSize(settings.storageUsed)} used of {formatFileSize(settings.storageLimit)}
            </p>
          </div>
        </div>
        <p className="storage-note">
          Note: Storage is only used for file metadata and organization structure. 
          Your 3D models remain stored on MyMiniFactory's servers.
        </p>
      </div>
      
      <div className="settings-actions">
        <button 
          onClick={saveSettings} 
          className="save-button"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default Settings;